import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/base/PageSEO';

type ArticleSectionData = {
  type: 'intro' | 'h2' | 'h3' | 'paragraph' | 'table' | 'checklist' | 'cta';
  title?: string;
  content?: string;
  items?: string[];
  tableRows?: { cells: string[] }[];
  tableHeaders?: string[];
};

type ArticleLayoutProps = {
  title: string;
  description: string;
  date: string;
  readTime: string;
  imageUrl: string;
  sections: ArticleSectionData[];
  keywords: string;
  canonicalPath: string;
  relatedArticles: { slug: string; title: string }[];
  faqItems?: { question: string; answer: string }[];
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.wtf';

export default function ArticleLayout({
  title,
  description,
  date,
  readTime,
  imageUrl,
  sections,
  keywords,
  canonicalPath,
  relatedArticles,
  faqItems,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fullUrl = `${SITE_URL}${canonicalPath}`;

  const blogPostingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description: description,
    image: imageUrl,
    datePublished: '2026-06-17',
    dateModified: '2026-06-17',
    author: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'CSSBuy Spreadsheet',
      url: SITE_URL,
    },
    url: fullUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Hub Principal',
        item: SITE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: title,
        item: fullUrl,
      },
    ],
  };

  const allJsonLd = faqItems
    ? [
        blogPostingJsonLd,
        breadcrumbJsonLd,
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ]
    : [blogPostingJsonLd, breadcrumbJsonLd];

  return (
    <main className="min-h-screen bg-background-50 pt-20">
      <PageSEO
        title={title}
        description={description}
        canonicalPath={canonicalPath}
        keywords={keywords}
        jsonLd={allJsonLd}
      />
      {/* Breadcrumb */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 pb-0">
        <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-4" aria-label="Breadcrumb">
          <Link
            to="/"
            className="hover:text-foreground-700 transition-colors inline-flex items-center gap-1 cursor-pointer"
          >
            <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
            Hub Principal
          </Link>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          <span className="text-foreground-600 truncate max-w-[200px]">{title}</span>
        </nav>
      </div>

      <article className="max-w-4xl mx-auto px-4 md:px-6 pb-16">
        {/* Header */}
        <header className="mb-8">
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
            {title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400 mb-6">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
              Atualizado em {date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
              {readTime} de leitura
            </span>
          </div>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
            {description}
          </p>
        </header>

        {/* Featured Image */}
        <div className="rounded-2xl overflow-hidden mb-10">
          <img
            src={imageUrl}
            alt={title}
            title={`${title} - CSSBuy Spreadsheet`}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>

        {/* Content Sections */}
        {sections.map((section, i) => {
          if (section.type === 'intro' || section.type === 'paragraph') {
            return (
              <div key={i} className="mb-8">
                <p className="text-base text-foreground-700 leading-relaxed">{section.content}</p>
              </div>
            );
          }

          if (section.type === 'h2') {
            return (
              <div key={i} className="mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {section.title}
                </h2>
                {section.content && (
                  <p className="text-base text-foreground-700 leading-relaxed">{section.content}</p>
                )}
              </div>
            );
          }

          if (section.type === 'h3') {
            return (
              <div key={i} className="mb-4 mt-6">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">
                  {section.title}
                </h3>
                {section.content && (
                  <p className="text-base text-foreground-700 leading-relaxed">{section.content}</p>
                )}
              </div>
            );
          }

          if (section.type === 'checklist' && section.items) {
            return (
              <div key={i} className="bg-white rounded-2xl p-6 border border-background-200/60 mb-6">
                {section.title && (
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">{section.title}</h3>
                )}
                <ul className="space-y-2.5">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-foreground-600">
                      <i className="ri-check-line w-4 h-4 flex items-center justify-center text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          if (section.type === 'table' && section.tableHeaders && section.tableRows) {
            return (
              <div key={i} className="overflow-x-auto rounded-2xl border border-background-200/60 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      {section.tableHeaders.map((h, j) => (
                        <th key={j} className="text-left px-5 py-3 font-semibold text-foreground-800 text-xs tracking-wide uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {section.tableRows.map((row, j) => (
                      <tr key={j} className="border-t border-background-100 hover:bg-background-50 transition-colors">
                        {row.cells.map((cell, k) => (
                          <td key={k} className="px-5 py-3 text-foreground-700">{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          }

          return null;
        })}

        {/* Internal Links */}
        <div className="mt-10 p-6 bg-white rounded-2xl border border-background-200/60">
          <h3 className="text-sm font-semibold text-foreground-800 uppercase tracking-wide mb-4">
            Links Úteis
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line w-4 h-4 flex items-center justify-center"></i>
              Hub Principal CSSBuy Spreadsheet
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
              Acessar Planilha Completa
            </a>
            {relatedArticles.slice(0, 3).map((ra, j) => (
              <Link
                key={j}
                to={`/${ra.slug}`}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-article-line w-4 h-4 flex items-center justify-center"></i>
                {ra.title.length > 35 ? ra.title.substring(0, 35) + '...' : ra.title}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-foreground-950 rounded-3xl p-8 md:p-10 text-center mt-10">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
            Pronto para Começar a Economizar?
          </h2>
          <p className="text-white/60 text-sm md:text-base mb-6 max-w-lg mx-auto leading-relaxed">
            Acesse agora a planilha completa com centenas de produtos verificados, atualizada diariamente para você.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 bg-white text-foreground-950 text-sm font-semibold rounded-md hover:bg-background-100 transition-colors cursor-pointer"
            >
              Acessar Planilha Agora
              <i className="ri-arrow-right-up-line ml-2 w-4 h-4 flex items-center justify-center"></i>
            </a>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center justify-center px-8 py-3.5 border-2 border-white/30 text-white text-sm font-semibold rounded-md hover:border-white/60 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              Voltar ao Hub Principal
            </Link>
          </div>
        </div>
      </article>
    </main>
  );
}